import i18n from '@/i18n';
import { useAuthStore } from '@/stores/useAuthStore';

export const getAuthStore = () => useAuthStore();

export const normalizeSegmentKey = (value) => {
  if (!value) return value;
  return value
    .toString()
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase();
};

/**
 * Guard helper: allow entry only if `callback()` is truthy, otherwise redirect
 * to `returnName` (defaults to the profile page).
 */
export const onlyIf = (callback, returnName = 'profile') => ({
  beforeEnter: () => {
    if (callback()) {
      return null;
    }
    return { name: returnName };
  },
});

const benchmarkListPage = () => import('../../features/benchmarks/pages/BenchmarkListPage.vue');
const benchmarkWizardPage = () => import('../../features/benchmarks/pages/BenchmarkWizardPage.vue');

const benchmarkMeta = ({ title, featureFlag, entitlement }) => ({
  cauldron: 'executive',
  title,
  requiresAuth: true,
  ...(featureFlag ? { requiresFeatureFlag: featureFlag } : {}),
  ...(entitlement
    ? { requiresEntitlement: Array.isArray(entitlement) ? [...entitlement] : entitlement }
    : {}),
});

// Creates the stable list/new/run route trio for one benchmark tool.
export const createBenchmarkRoutes = ({
  benchmarkKey,
  basePath,
  routeName,
  titleKey,
  featureFlag,
  entitlement,
  favoritableId,
}) => {
  const title = i18n.global.t(titleKey);
  const createBaseBreadcrumbs = () => [
    { label: i18n.global.t('menu_tools'), to: '/tools' },
    { label: title, to: basePath },
  ];
  const createBaseMeta = () => benchmarkMeta({ title, featureFlag, entitlement });

  return [
    {
      meta: {
        ...createBaseMeta(),
        ...(favoritableId ? { favoritable: { kind: 'tool', id: favoritableId } } : {}),
        breadcrumbs: createBaseBreadcrumbs(),
      },
      path: basePath,
      name: routeName,
      component: benchmarkListPage,
      props: { benchmarkKey },
    },
    {
      meta: {
        ...createBaseMeta(),
        breadcrumbs: [
          ...createBaseBreadcrumbs(),
          {
            label: i18n.global.t('tools.benchmarks.wizard.new_run'),
            to: `${basePath}/new`,
          },
        ],
      },
      path: `${basePath}/new`,
      name: `${routeName}-new`,
      component: benchmarkWizardPage,
      props: { benchmarkKey, mode: 'new' },
    },
    {
      meta: {
        ...createBaseMeta(),
        breadcrumbs: createBaseBreadcrumbs(),
      },
      path: `${basePath}/runs/:runId`,
      name: `${routeName}-run`,
      component: benchmarkWizardPage,
      props: (route) => ({ benchmarkKey, mode: 'run', runId: route.params.runId }),
    },
  ];
};
